import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UIButton } from '../../features/ui-system';
import { Input } from '../../components/ui/input';
import AdminPackageMenuLayout from './components/AdminPackageMenuLayout';
import { API, fetchOwners, getHeaders, toNumber } from './utils/packageManagementApi';

const emptyForm = {
  id: '',
  name: '',
  owner_id: '',
  cost_price: 0,
  reseller_fee: 0,
  price: 0,
  speed_mbps: '',
  active_value: '',
  active_unit: 'day',
  active_days: 0,
  duration_value: '',
  duration_unit: 'minute',
  duration_minutes: 0,
  quota_mb: 0,
  limit_mode: 'time',
  notes: '',
};

const mapButtonVariant = (variant) => {
  if (variant === 'outline' || variant === 'secondary') return 'subtle';
  if (variant === 'danger') return 'danger';
  if (variant === 'ghost') return 'ghost';
  return 'primary';
};

const Button = ({ variant = 'primary', className = '', ...props }) => (
  <UIButton variant={mapButtonVariant(variant)} className={className} {...props} />
);

const readMeta = (notes) => {
  try {
    const txt = String(notes || '');
    if (!txt.startsWith('__meta__:')) return { owner_id: '', notes: txt };
    const raw = txt.replace('__meta__:', '');
    const parsed = JSON.parse(raw);
    return { owner_id: parsed.owner_id || '', notes: parsed.notes || '' };
  } catch {
    return { owner_id: '', notes: String(notes || '') };
  }
};

const writeMeta = (owner_id, notes) => JSON.stringify({ owner_id: owner_id || '', notes: notes || '' });
const calculateResellerFee = (costPrice, sellPrice) => {
  const cost = toNumber(costPrice);
  const price = toNumber(sellPrice);
  return Math.max(0, price - cost);
};
const minutesByUnit = (value, unit) => {
  const v = Math.max(0, toNumber(value));
  if (unit === 'hour') return Math.round(v * 60);
  if (unit === 'day') return Math.round(v * 1440);
  if (unit === 'month') return Math.round(v * 43200);
  return Math.round(v);
};
const daysByUnit = (value, unit) => {
  const v = Math.max(0, toNumber(value));
  if (unit === 'month') return Math.round(v * 30);
  if (unit === 'hour') return Math.ceil((v * 60) / 1440);
  if (unit === 'minute') return Math.ceil(v / 1440);
  return Math.round(v);
};
const splitMinutesToUnit = (minutesRaw) => {
  const minutes = Math.max(0, toNumber(minutesRaw));
  if (minutes > 0 && minutes % 43200 === 0) return { value: minutes / 43200, unit: 'month' };
  if (minutes > 0 && minutes % 1440 === 0) return { value: minutes / 1440, unit: 'day' };
  if (minutes > 0 && minutes % 60 === 0) return { value: minutes / 60, unit: 'hour' };
  return { value: minutes, unit: 'minute' };
};
const splitDaysToUnit = (daysRaw) => {
  const days = Math.max(0, toNumber(daysRaw));
  if (days > 0 && days % 30 === 0) return { value: days / 30, unit: 'month' };
  return { value: days, unit: 'day' };
};

export default function AdminHotspotProfileManagement() {
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [res, ownerItems] = await Promise.all([
        axios.get(`${API}/service-profiles`, { headers: getHeaders() }),
        fetchOwners(),
      ]);
      const all = Array.isArray(res.data?.items) ? res.data.items : (Array.isArray(res.data) ? res.data : []);
      const mapped = all
        .filter((x) => String(x.service_type || '').toLowerCase() === 'hotspot')
        .map((x) => {
          const meta = readMeta(x.notes);
          return { ...x, owner_id: meta.owner_id, notes_plain: meta.notes };
        });
      setItems(mapped);
      setOwners(ownerItems);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat profil hotspot');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    const nextFee = calculateResellerFee(form.cost_price, form.price);
    setForm((prev) => {
      if (toNumber(prev.reseller_fee) === nextFee) return prev;
      return { ...prev, reseller_fee: nextFee };
    });
  }, [form.cost_price, form.price]);

  const filtered = useMemo(() => {
    const key = search.trim().toLowerCase();
    if (!key) return items;
    return items.filter((it) => `${it.name || ''}`.toLowerCase().includes(key));
  }, [items, search]);

  const ownerName = (ownerId) => owners.find((o) => o.id === ownerId)?.name || '-';

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Nama profil hotspot wajib diisi');
      return;
    }
    if (!form.owner_id) {
      toast.error('Owner / Reseller wajib dipilih');
      return;
    }
    if (toNumber(form.price) < toNumber(form.cost_price)) {
      toast.error('Harga jual tidak boleh lebih kecil dari harga modal');
      return;
    }
    if (toNumber(form.speed_mbps) <= 0) {
      toast.error('Kecepatan (Mbps) wajib diisi dan harus lebih dari 0');
      return;
    }
    if (toNumber(form.active_value) <= 0) {
      toast.error('Masa aktif wajib diisi dan harus lebih dari 0');
      return;
    }
    const nextDurationMinutes = minutesByUnit(form.duration_value, form.duration_unit);
    if ((form.limit_mode === 'time' || form.limit_mode === 'both') && nextDurationMinutes <= 0) {
      toast.error('Durasi wajib diisi untuk mode Limit Waktu atau Kombinasi');
      return;
    }
    const nextQuotaMb = toNumber(form.quota_mb);
    if ((form.limit_mode === 'quota' || form.limit_mode === 'both') && nextQuotaMb <= 0) {
      toast.error('Kuota wajib diisi untuk mode Limit Kuota atau Kombinasi');
      return;
    }

    const payload = {
      name: form.name.trim(),
      service_type: 'hotspot',
      owner_id: form.owner_id || null,
      cost_price: toNumber(form.cost_price),
      reseller_fee: calculateResellerFee(form.cost_price, form.price),
      price: toNumber(form.price, toNumber(form.cost_price) + calculateResellerFee(form.cost_price, form.price)),
      speed_mbps: toNumber(form.speed_mbps),
      active_days: daysByUnit(form.active_value, form.active_unit),
      duration_minutes: form.limit_mode === 'quota' ? 0 : nextDurationMinutes,
      quota_mb: form.limit_mode === 'time' ? 0 : nextQuotaMb,
      limit_mode: form.limit_mode || 'time',
      notes: `__meta__:${writeMeta(form.owner_id, form.notes)}`,
    };

    setSaving(true);
    try {
      if (form.id) {
        await axios.put(`${API}/service-profiles/${form.id}`, payload, { headers: getHeaders() });
        toast.success('Profil hotspot diperbarui');
      } else {
        await axios.post(`${API}/service-profiles`, payload, { headers: getHeaders() });
        toast.success('Profil hotspot ditambahkan');
      }
      setForm(emptyForm);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan profil hotspot');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Hapus profil hotspot ini?')) return;
    try {
      await axios.delete(`${API}/service-profiles/${id}`, { headers: getHeaders() });
      toast.success('Profil hotspot dihapus');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus profil hotspot');
    }
  };

  return (
    <AdminPackageMenuLayout
      eyebrow="Voucher"
      title="Profil Hotspot"
      subtitle="Kelola master profil hotspot untuk generate voucher: harga, kecepatan, durasi, kuota, dan limit pemakaian."
      activePath="/dadu/manajemen/hotspot-profile"
    >
      <div className="space-y-6">
        <div className="ktc-form-shell rounded-2xl border border-white/10 bg-[#121212] p-5 shadow-sm">
          <h2 className="text-white font-semibold mb-4">{form.id ? 'Edit Profil Hotspot' : 'Tambah Profil Hotspot'}</h2>
          <form className="space-y-3" onSubmit={onSubmit}>
            <div className="rounded border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-[11px] text-cyan-200">
              Isi profil hotspot sebagai master paket. Nilai ini dipakai saat generate voucher dan aktivasi user hotspot.
            </div>
            <p className="text-xs font-medium text-gray-300">Nama Profil *</p>
            <Input placeholder="Nama Profil" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            <p className="text-[11px] text-gray-400">Contoh: Hotspot 2 Jam, Hotspot 1 Hari, Hotspot 10GB.</p>

            <p className="text-xs font-medium text-gray-300">Owner Data</p>
            <select className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded text-white" value={form.owner_id || 'none'} onChange={(e) => setForm((p) => ({ ...p, owner_id: e.target.value === 'none' ? '' : e.target.value }))}>
              <option value="none">- Pilih Owner Data -</option>
              {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <p className="text-[11px] text-gray-400">Opsional, dipakai untuk pengelompokan owner/reseller.</p>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-300">Harga Modal (Rp)</p>
                <Input type="number" placeholder="Harga Modal" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.cost_price} onChange={(e) => setForm((p) => ({ ...p, cost_price: e.target.value }))} />
                <p className="text-[11px] text-gray-400">Biaya pokok paket dari sisi operasional.</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-300">Fee Reseller (Rp)</p>
                <div className="rounded-md border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
                  Rp {toNumber(form.reseller_fee).toLocaleString('id-ID')}
                </div>
                <p className="text-[11px] text-gray-400">Otomatis dari selisih Harga Jual - Harga Modal (tanpa input manual).</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-300">Harga Jual (Rp)</p>
                <Input type="number" placeholder="Harga Jual" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.price} onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))} />
                <p className="text-[11px] text-gray-400">Jika dikosongkan, sistem gunakan Modal + Fee Reseller.</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-300">Kecepatan (Mbps)</p>
                <Input type="number" min={1} required placeholder="Contoh kecepatan (Mbps)" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.speed_mbps} onChange={(e) => setForm((p) => ({ ...p, speed_mbps: e.target.value }))} />
                <p className="text-[11px] text-gray-400">Wajib diisi. Isi angka kecepatan profil hotspot (Mbps).</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-300">Masa Aktif</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" min={1} required placeholder="Contoh: 1" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.active_value} onChange={(e) => setForm((p) => ({ ...p, active_value: e.target.value }))} />
                  <select className="px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded text-white" value={form.active_unit} onChange={(e) => setForm((p) => ({ ...p, active_unit: e.target.value }))}>
                    <option value="minute">Menit</option>
                    <option value="hour">Jam</option>
                    <option value="day">Hari</option>
                    <option value="month">Bulan</option>
                  </select>
                </div>
                <p className="text-[11px] text-gray-400">Disimpan ke backend sebagai hari. Unit menit/jam akan dibulatkan ke atas ke hari terdekat.</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-300">Tipe Profil</p>
                <select className="px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded text-white" value={form.limit_mode} onChange={(e) => setForm((p) => ({ ...p, limit_mode: e.target.value }))}>
                  <option value="time">Limit Waktu</option>
                  <option value="quota">Limit Kuota</option>
                  <option value="both">Kombinasi</option>
                </select>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">Pilih tipe sesuai model paket: waktu, kuota, atau kombinasi.</p>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-300">Durasi</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" min={1} placeholder="Contoh: 5" disabled={form.limit_mode === 'quota'} className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed" value={form.duration_value} onChange={(e) => setForm((p) => ({ ...p, duration_value: e.target.value }))} />
                  <select disabled={form.limit_mode === 'quota'} className="px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed" value={form.duration_unit} onChange={(e) => setForm((p) => ({ ...p, duration_unit: e.target.value }))}>
                    <option value="minute">Menit</option>
                    <option value="hour">Jam</option>
                    <option value="day">Hari</option>
                    <option value="month">Bulan</option>
                  </select>
                </div>
                <p className="text-[11px] text-gray-400">Dipakai untuk mode Limit Waktu atau Kombinasi.</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-300">Kuota (MB)</p>
                <Input type="number" placeholder="Contoh: 2048" disabled={form.limit_mode === 'time'} className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed" value={form.quota_mb} onChange={(e) => setForm((p) => ({ ...p, quota_mb: e.target.value }))} />
                <p className="text-[11px] text-gray-400">Dipakai untuk mode Limit Kuota atau Kombinasi.</p>
              </div>
            </div>

            <p className="text-xs font-medium text-gray-300">Catatan</p>
            <Input placeholder="Catatan" className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />

            <div className="flex gap-2">
              <Button type="submit" className="btn-primary text-white" disabled={saving}>{saving ? 'Menyimpan...' : (form.id ? 'Update Profil' : 'Tambah Profil')}</Button>
              {form.id && <Button type="button" variant="outline" className="btn-secondary" onClick={() => setForm(emptyForm)}>Batal</Button>}
            </div>
          </form>
        </div>

        <div className="ktc-table-card rounded-2xl border border-white/10 bg-[#121212] p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3 gap-3">
            <h2 className="text-white font-semibold">Daftar Profil Hotspot</h2>
            <Input placeholder="Cari..." className="w-64 bg-[#0A0A0A] border-white/10 text-white placeholder:text-slate-400" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="ktc-table-shell overflow-x-auto">
            <table className="ktc-data-table w-full text-sm">
              <thead>
                <tr className="text-left text-gray-300 border-b border-white/10">
                  <th className="py-2">Nama Profil</th>
                  <th className="py-2">Harga Modal</th>
                  <th className="py-2">Harga Jual</th>
                  <th className="py-2">Masa Aktif</th>
                  <th className="py-2">Owner</th>
                  <th className="py-2">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td className="py-4 text-gray-400" colSpan={6}>Memuat...</td></tr>}
                {!loading && filtered.length === 0 && <tr><td className="py-4 text-gray-400" colSpan={6}>Belum ada data</td></tr>}
                {!loading && filtered.map((it) => (
                  <tr key={it.id} className="border-b border-white/10">
                    <td className="py-2 text-white">{it.name}</td>
                    <td className="py-2 text-gray-300">Rp {Number(it.cost_price || 0).toLocaleString('id-ID')}</td>
                    <td className="py-2 text-gray-300">Rp {Number(it.price || 0).toLocaleString('id-ID')}</td>
                    <td className="py-2 text-gray-300">{it.active_days || 0} hari</td>
                    <td className="py-2 text-gray-300">{ownerName(it.owner_id)}</td>
                    <td className="py-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-secondary"
                        onClick={() => {
                          const activeSplit = splitDaysToUnit(it.active_days);
                          const durationSplit = splitMinutesToUnit(it.duration_minutes);
                          setForm({
                            ...emptyForm,
                            ...it,
                            active_value: activeSplit.value,
                            active_unit: activeSplit.unit,
                            duration_value: durationSplit.value,
                            duration_unit: durationSplit.unit,
                            notes: it.notes_plain || '',
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => onDelete(it.id)}>Hapus</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminPackageMenuLayout>
  );
}


